import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkHandoff } from '@shared/types/thread-work';
import { TodayObjectives } from '@/components/objectives/TodayObjectives';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { loadMemberFile, loadTeamWeek, type MemberFile as MemberFileData, type TeamMemberWeek, weekStart } from '@/lib/team-scope';
import { buildHandoffsView, loadHandoffs } from '@/lib/work-handoffs';
import { cn } from '@/lib/utils';
import { MemberFile } from './MemberFile';
import { summarizeFile } from './team-week';

const CELL = 'px-3 py-2.5 tabular-nums';

/**
 * Le point du lundi (team scope, 21/10/2026): the measured objectives, then one
 * row per person — their tasks, the oldest one, the objects waiting in their
 * launches, the relays waiting for them, what they did this week. A row opens
 * their file as they see it.
 */
export const TeamWeek = () => {
  const now = useMemo(() => new Date(), []);
  const [members, setMembers] = useState<TeamMemberWeek[] | null>(null);
  const [files, setFiles] = useState<Record<string, MemberFileData | null>>({});
  const [handoffs, setHandoffs] = useState<WorkHandoff[]>([]);
  const [opened, setOpened] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [team, relays] = await Promise.all([loadTeamWeek(weekStart(now)), loadHandoffs()]);
      setMembers(team);
      setHandoffs(relays);
      const loaded = await Promise.all(team.map(async (member) => [member.userId, await loadMemberFile(member.userId).catch(() => null)] as const));
      setFiles(Object.fromEntries(loaded));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La semaine de l’équipe n’est pas accessible.');
    }
  }, [now]);
  useEffect(() => { void load(); }, [load]);

  const people = (members ?? []).map((member) => ({ userId: member.userId, name: member.name }));
  const summaries = Object.fromEntries((members ?? []).map((member) => {
    const file = files[member.userId];
    return [member.userId, file ? summarizeFile(file, buildHandoffsView(handoffs, member.userId, people), now) : null];
  }));
  const nameOf = (userId: string) => people.find((person) => person.userId === userId)?.name ?? 'Un collègue';

  if (error) return <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>;
  return (
    <div className="space-y-4">
      <TodayObjectives />
      {!members ? (
        <SkeletonGroup label="Chargement de l’équipe" className="space-y-2">
          {[0, 1, 2].map((index) => <Skeleton key={index} className="h-10 w-full" />)}
        </SkeletonGroup>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-domino-cardBorder bg-domino-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-domino-inkSoft">
                <th className="px-3 py-2 font-medium">Personne</th>
                <th className="px-3 py-2 font-medium">Tâches</th>
                <th className="px-3 py-2 font-medium">La plus ancienne</th>
                <th className="px-3 py-2 font-medium">Fiches à traiter</th>
                <th className="px-3 py-2 font-medium">Relais reçus</th>
                <th className="px-3 py-2 font-medium">Fait cette semaine</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const summary = summaries[member.userId];
                const open = opened === member.userId;
                return (
                  <Fragment key={member.userId}>
                    <tr className={cn('cursor-pointer border-t border-domino-cardBorder hover:bg-domino-muted/30', open && 'bg-domino-lilac/30')}
                      onClick={() => setOpened(open ? null : member.userId)}>
                      <td className="px-3 py-2.5">
                        <button type="button" aria-expanded={open} className="font-semibold text-domino-aubergine hover:underline">{member.name}</button>
                      </td>
                      {summary ? (
                        <>
                          <td className={CELL}>{summary.tasks}</td>
                          <td className={CELL}>{summary.oldestDays === null ? '' : `${summary.oldestDays} j`}</td>
                          <td className={CELL}>{summary.objects || ''}</td>
                          <td className={CELL}>{summary.incoming.length || ''}</td>
                        </>
                      ) : <td colSpan={4} className="px-3 py-2.5"><Skeleton className="h-4 w-40" /></td>}
                      <td className={CELL}>{member.done || ''}</td>
                    </tr>
                    {open && summary && (
                      <tr>
                        <td colSpan={6} className="border-t border-domino-cardBorder bg-domino-ivory px-3 py-3">
                          <MemberFile name={member.name} summary={summary} nameOf={nameOf} onChange={() => void load()} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
